import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Truck, MapPin, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { free_shipping_threshold, shipping_fee, outside_zone_fee, allowed_cities } = useSettings();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const CITIES = (allowed_cities && allowed_cities.length ? allowed_cities : ["Delhi NCR"]);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    line1: "",
    line2: "",
    city: CITIES[0],
    state: "Delhi",
    pincode: "",
    landmark: "",
    notes: "",
    payment_method: "cod",
  });
  const [couponCode, setCouponCode] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount, description}

  const inZone = useMemo(() => {
    const c = form.city.trim().toLowerCase();
    return CITIES.some((a) => {
      const al = a.toLowerCase();
      return c === al || al.includes(c) || c.includes(al);
    });
  }, [form.city, CITIES]);
  const threshold = free_shipping_threshold || 2000;
  const fee = shipping_fee || 49;
  const outside = outside_zone_fee || 98;
  const shipping = subtotal === 0 ? 0 : inZone ? (subtotal >= threshold ? 0 : fee) : outside;
  const discount = appliedCoupon?.discount || 0;
  const total = Math.max(0, subtotal + shipping - discount);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) { toast.error("Enter a coupon code"); return; }
    setApplyingCoupon(true);
    try {
      const res = await api.post("/coupons/validate", { code, subtotal });
      setAppliedCoupon(res.data);
      toast.success(`${res.data.code} applied — you save ${formatINR(res.data.discount)}`);
    } catch (e) {
      setAppliedCoupon(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name";
    if (!/^[0-9]{10}$/.test(form.phone.trim())) return "Please enter a valid 10-digit phone";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email";
    if (!form.line1.trim()) return "Please enter your address";
    if (!form.city.trim()) return "Please enter your city";
    if (!/^[0-9]{6}$/.test(form.pincode.trim())) return "Please enter a valid 6-digit pincode";
    return null;
  };

  const placeOrder = async () => {
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const orderPayload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          weight: i.weight,
          unit_price: i.unit_price,
          quantity: i.quantity,
          is_gift_box: !!i.is_gift_box,
          gift_box_items: i.gift_box_items || null,
          gift_note: i.gift_note || "",
          is_bulk_box: !!i.is_bulk_box,
          bulk_box_items: i.bulk_box_items || null,
        })),
        address: {
          full_name: form.full_name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || null,
          line1: form.line1.trim(),
          line2: form.line2.trim(),
          city: form.city.trim(),
          state: form.state.trim() || "Delhi",
          pincode: form.pincode.trim(),
          landmark: form.landmark.trim(),
        },
        payment_method: form.payment_method,
        notes: form.notes.trim(),
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
      };

      if (form.payment_method === "razorpay") {
        // 1. Create Razorpay order
        const createRes = await api.post("/orders/razorpay/create", orderPayload);
        const { razorpay_order_id, key_id, amount, currency } = createRes.data;
        if (!window.Razorpay) { toast.error("Payment SDK not loaded — refresh and try again"); setSubmitting(false); return; }
        // 2. Open Razorpay checkout modal
        const rzp = new window.Razorpay({
          key: key_id,
          amount,
          currency,
          order_id: razorpay_order_id,
          name: "Dadu's Achaar",
          description: "Homemade organic pickles",
          image: "/logo.jpeg",
          prefill: {
            name: orderPayload.address.full_name,
            email: orderPayload.address.email || "",
            contact: orderPayload.address.phone,
          },
          theme: { color: "#9E3B21" },
          modal: {
            ondismiss: () => {
              toast.info("Payment cancelled — you can try again");
              setSubmitting(false);
            },
          },
          handler: async (response) => {
            // 3. Verify signature on server
            try {
              const verifyRes = await api.post("/orders/razorpay/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_payload: orderPayload,
              });
              clear();
              toast.success("Payment successful! Order confirmed.");
              navigate(`/order-success/${verifyRes.data.order_number}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment verification failed. Please contact support.");
              setSubmitting(false);
            }
          },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp?.error?.description || "Payment failed. Please try again.");
          setSubmitting(false);
        });
        rzp.open();
        // NOTE: setSubmitting(false) happens in handler / ondismiss / payment.failed
        return;
      }

      // COD path (default)
      const res = await api.post("/orders", orderPayload);
      clear();
      toast.success("Order placed! We'll contact you shortly.");
      navigate(`/order-success/${res.data.order_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order. Please try again.");
      setSubmitting(false);
      return;
    } finally {
      if (form.payment_method !== "razorpay") setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <p className="font-serif text-3xl mb-3">Your cart is empty</p>
        <p className="text-[#6E675F] mb-6">Add some pickles before checking out.</p>
        <Button data-testid="checkout-empty-shop" onClick={() => navigate("/shop")} className="btn-primary rounded-full px-8">
          Browse Pickles
        </Button>
      </div>
    );
  }

  return (
    <div className="fade-up max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-8">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Address + Payment */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <MapPin className="w-5 h-5 text-[#9E3B21]" />
              <h2 className="font-serif text-xl font-semibold">Delivery Address</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Full name</Label>
                <Input data-testid="address-input-name" value={form.full_name} onChange={update("full_name")} placeholder="Aarav Sharma" className="mt-1.5" />
              </div>
              <div>
                <Label>Phone (10-digit)</Label>
                <Input data-testid="address-input-phone" value={form.phone} onChange={update("phone")} placeholder="9876543210" maxLength={10} className="mt-1.5" />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input data-testid="address-input-email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address line 1</Label>
                <Input data-testid="address-input-line1" value={form.line1} onChange={update("line1")} placeholder="House / flat, street" className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address line 2 (optional)</Label>
                <Input data-testid="address-input-line2" value={form.line2} onChange={update("line2")} placeholder="Area, sector" className="mt-1.5" />
              </div>
              <div>
                <Label>City / Zone</Label>
                <Select value={form.city} onValueChange={(v) => setForm((f) => ({ ...f, city: v }))}>
                  <SelectTrigger data-testid="address-input-city" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map((c) => <SelectItem key={c} value={c} data-testid={`city-option-${c.toLowerCase().replace(/\s/g,'-')}`}>{c}</SelectItem>)}
                    <SelectItem value="Other" data-testid="city-option-other">Other (outside zone)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pincode</Label>
                <Input data-testid="address-input-pincode" value={form.pincode} onChange={update("pincode")} placeholder="110001" maxLength={6} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Landmark (optional)</Label>
                <Input data-testid="address-input-landmark" value={form.landmark} onChange={update("landmark")} placeholder="Near..." className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Order notes (optional)</Label>
                <Textarea data-testid="address-input-notes" value={form.notes} onChange={update("notes")} placeholder="Anything we should know?" className="mt-1.5" />
              </div>
            </div>
            {!inZone && form.city && (
              <p className="mt-4 text-xs text-[#D97706] bg-[#D97706]/10 rounded-lg p-3">
                Note: We deliver primarily to {CITIES.join(" / ")}. Orders outside will attract extra shipping ({formatINR(outside)}).
              </p>
            )}
          </div>

          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <ShieldCheck className="w-5 h-5 text-[#9E3B21]" />
              <h2 className="font-serif text-xl font-semibold">Payment Method</h2>
            </div>
            <RadioGroup value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
              <label htmlFor="pm-cod" className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${form.payment_method === "cod" ? "border-[#9E3B21] bg-[#9E3B21]/5" : "border-[#E6DEC3]"}`}>
                <RadioGroupItem value="cod" id="pm-cod" data-testid="payment-method-cod" />
                <div>
                  <div className="font-semibold text-[#1E1B18]">Cash on Delivery</div>
                  <div className="text-xs text-[#6E675F] mt-0.5">Pay in cash when your jars arrive at your doorstep.</div>
                </div>
              </label>
              <label htmlFor="pm-razorpay" className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${form.payment_method === "razorpay" ? "border-[#9E3B21] bg-[#9E3B21]/5" : "border-[#E6DEC3]"}`}>
                <RadioGroupItem value="razorpay" id="pm-razorpay" data-testid="payment-method-razorpay" />
                <div>
                  <div className="font-semibold text-[#1E1B18]">Online Payment <span className="text-xs font-normal text-[#5F7355]">(Instant · Secured by Razorpay)</span></div>
                  <div className="text-xs text-[#6E675F] mt-0.5">UPI · Debit/Credit Cards · Netbanking · Wallets — 0% fee on UPI.</div>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E6DEC3] rounded-2xl p-6 sticky top-24">
            <h2 className="font-serif text-xl font-semibold mb-4">Order Summary</h2>
            <ul className="space-y-3 mb-4 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <li key={`${i.product_id}-${i.weight}`} className="flex justify-between text-sm">
                  <div>
                    <div className="font-medium">{i.product_name}</div>
                    <div className="text-xs text-[#6E675F]">{i.weight} × {i.quantity}</div>
                  </div>
                  <div className="font-semibold">{formatINR(i.unit_price * i.quantity)}</div>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#E6DEC3] pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#6E675F]">
                <span>Subtotal</span>
                <span data-testid="summary-subtotal">{formatINR(subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#6E675F]">
                <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Shipping</span>
                <span data-testid="summary-shipping">{shipping === 0 ? "FREE" : formatINR(shipping)}</span>
              </div>
              {appliedCoupon ? (
                <div className="flex justify-between text-[#2D6A4F]">
                  <span className="flex items-center gap-1">
                    Coupon {appliedCoupon.code}
                    <button onClick={removeCoupon} className="text-[10px] text-[#9E3B21] hover:underline ml-1" data-testid="coupon-remove">remove</button>
                  </span>
                  <span data-testid="summary-discount">− {formatINR(discount)}</span>
                </div>
              ) : (
                <div className="pt-1">
                  <div className="flex gap-2">
                    <Input
                      data-testid="coupon-input"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Have a coupon? Enter code"
                      className="text-xs h-9"
                    />
                    <Button
                      data-testid="coupon-apply"
                      variant="outline"
                      size="sm"
                      onClick={applyCoupon}
                      disabled={applyingCoupon || !couponCode.trim()}
                      className="h-9 rounded-full text-xs"
                    >
                      {applyingCoupon ? "…" : "Apply"}
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-[#E6DEC3] font-serif text-xl font-semibold">
                <span>Total</span>
                <span data-testid="summary-total">{formatINR(total)}</span>
              </div>
            </div>
            <Button
              data-testid="place-order-btn"
              onClick={placeOrder}
              disabled={submitting}
              className="w-full btn-primary rounded-full mt-6 py-6 text-base"
            >
              {submitting ? "Placing order..." : "Place Order"}
            </Button>
            <p className="text-xs text-center text-[#6E675F] mt-3">By placing your order, you agree to our terms.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
